import React, { useCallback, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  getInitialYearValue,
  getYearsPeriod,
  onNextYearPeriod,
  onPreviousYearPeriod,
  PeriodsProps,
  YearKeyValuePair,
  YearSelectionArray,
} from './utils';
import PeriodSlider from './PeriodSlider';
import CustomPeriodPicker from './CustomPeriodPicker';

const yearFormatter = (year: number): string => year.toString();
const currentYear = new Date().getFullYear();

const CustomYear: React.FC<PeriodsProps> = ({
  startDate: start,
  endDate: end,
  reportYear,
  showMoreYears,
  isThirdParty,
  onChangePeriod,
}) => {
  const [years, setYears] = useState<YearSelectionArray>(
    getYearsPeriod(yearFormatter, start?.getFullYear()),
  );
  const [startDate, setStartDate] = useState<Date | undefined>(
    start || new Date(Number(reportYear) || currentYear, 0, 1),
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    end || new Date(Number(reportYear) || currentYear, 11, 31),
  );

  const [selectedYear, setSelectedYear] = useState<
    YearKeyValuePair | undefined
  >(getInitialYearValue(start, end, yearFormatter));

  const handleYearChange = useCallback(
    (year: YearKeyValuePair) => {
      setSelectedYear(year);
      const startDate = new Date(year.value, 0, 1);
      const endDate = new Date(year.value, 11, 31);
      onChangePeriod({
        option: 'customYear',
        startDate,
        endDate,
      });
      setStartDate(startDate);
      setEndDate(endDate);
    },
    [setSelectedYear, setStartDate, setEndDate],
  );

  const handleChange = useCallback(
    (startDate: Date | undefined, endDate: Date | undefined) => {
      onChangePeriod({ option: 'customYear', startDate, endDate });
    },
    [onChangePeriod],
  );

  const handleStartDate = useCallback(
    (start: Date | null | undefined) => {
      if (!start || !endDate || start > endDate) {
        handleChange(undefined, undefined);
      } else {
        handleChange(start, endDate);
      }
      start && setStartDate(start);
    },
    [endDate, setStartDate, handleChange],
  );
  const handleEndDate = useCallback(
    (end: Date | null | undefined) => {
      if (!startDate || !end || startDate > end) {
        handleChange(undefined, undefined);
      } else {
        handleChange(startDate, end);
      }
      end && setEndDate(end);
    },
    [startDate, setEndDate, handleChange],
  );

  const handlePrevious = useCallback(() => {
    setYears(y => onPreviousYearPeriod(y, yearFormatter));
  }, [setYears]);
  const handleNext = useCallback(() => {
    setYears(y => onNextYearPeriod(y, yearFormatter));
  }, [setYears]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {!showMoreYears && (
        <>
          <TrueImpactText variant="heading">Period</TrueImpactText>
          <PeriodSlider
            years={years}
            isThirdParty={isThirdParty}
            selectedYear={selectedYear}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            handleChange={handleYearChange}
          />
        </>
      )}
      <CustomPeriodPicker
        startDate={startDate}
        endDate={endDate}
        disableFuture={isThirdParty}
        handleStartDate={handleStartDate}
        handleEndDate={handleEndDate}
      />
    </LocalizationProvider>
  );
};

export default CustomYear;
